import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AuthService, OCC_USER_ID_ANONYMOUS, StateWithProcess } from '@spartacus/core';
import { Observable, interval, of } from 'rxjs';
import { delayWhen, filter, map, share, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { ActiveCartFacade } from '../../../features/cart/base';
import { CheckoutConfig } from '../../../features/checkout/base/config/checkout-config';
import { UserIdService } from '../../auth';
import { SearchAction } from '../../features/store/actions/search.actions';
import { GlobalMessageService, GlobalMessageType } from '../../global-message';
import {
  AvailableBuyersSearchParams,
  B2BComment,
  CartSimulation,
  CartType,
  LoaderError,
  Order,
  OrderApproval,
  OrderApprovalDecision,
  OrderApprovalDecisionValue,
  OrderApprovalSearchParams,
  OrderDetails,
  OrderProcessState,
  OrderProcessStatus,
  OrderStatus,
  Pagination,
  SimpleCustomer,
  Sort,
} from '../../model';
import { withdrawOn } from '../../util';
import { OrderApprovalConnector } from '../connectors/order-approval.connector';
import { OrderApprovalFacade, UserFacade } from '../facades';
import { PrincipalConfigurationService } from '../index';
import { UserActions } from '../store/actions';
import { UserSelectors } from '../store/selectors';
import { getSearchPaginationSelector } from '../store/selectors/order-approval.selector';
import { ORDER_APPROVALS, ORDER_APPROVAL_BUYERS, StateWithUser } from '../store/user-state';

/**
 * The service usage might seem a bit complex due to the fact that an order approval
 * has three different data representations depending on what state it is currently in.
 *
 * Why? because of how we have modeled on the server to not store certain SAP data in Hybris.
 *
 * If the order is PENDING_APPROVAL then we must display it as a CartSimulation.
 * If the order is APPROVED then we must display it as an SAP order details.
 * If the order is of any other state then we must display it as a generic Hybris Order.
 */
@Injectable()
export class OrderApprovalService implements OrderApprovalFacade {
  constructor(
    private activeCartService: ActiveCartFacade,
    private authService: AuthService,
    private config: CheckoutConfig,
    private store: Store<StateWithUser & StateWithProcess<any>>,
    private userIdService: UserIdService,
    private userService: UserFacade,
    private orderApprovalConnector: OrderApprovalConnector,
    private globalMessageService: GlobalMessageService,
    private principalConfigurationService: PrincipalConfigurationService
  ) {}

  /**
   * Initiate a load of the order details from SAP of a completed order
   *
   * USE for orders with status APPROVED
   *
   * @param sapOrderNumber of complete order
   */
  loadApprovedOrderApprovalDetails(sapOrderNumber: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.LoadApprovedOrderApprovalDetails({
            userId,
            sapOrderNumber,
          })
        ),
      () => {}
    );
  }

  /**
   * Get the latest loaded order details
   *
   * USE for orders with status APPROVED
   */
  getApprovedOrderApprovalDetails(sapOrderNumber: string): Observable<OrderDetails> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getApprovedOrderApprovalDetails(sapOrderNumber));
  }

  /**
   * Initiate a load of the order from Hybris
   *
   * USE for orders not in status APPROVED or PENDING_APPROVAL
   *
   * @param orderApprovalCode of complete order
   */
  loadOrderApproval(orderApprovalCode: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.LoadOrderApproval({
            userId,
            orderApprovalCode,
          })
        ),
      () => {}
    );
  }

  /**
   * Get the latest loaded order approval
   *
   * USE for orders not in status APPROVED or PENDING_APPROVAL
   */
  getOrderApproval(orderApprovalCode: string): Observable<Order> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getOrderApproval(orderApprovalCode));
  }

  /**
   * Recovers the order approval into the cart of the supplied cartId
   *
   * @param orderApprovalCode of order
   * @param cartId of the cart to merge contents of order approval to
   */
  recoverOrderApproval(orderApprovalCode: string, cartId: string): void {
    this.userIdService
      .takeUserId(true)
      .pipe(
        withLatestFrom(
          this.userService.isBuyer(),
          this.principalConfigurationService.isEnabled('alwaysApproveOrdersForUser'),
          this.principalConfigurationService.isEnabled('enablePurchaseLimitsUser')
        ),
        filter(
          ([_, isBuyer, hasAlwaysApproveOrdersForUser, isPurchaseLimitsUser]) =>
            isBuyer || hasAlwaysApproveOrdersForUser || isPurchaseLimitsUser
        )
      )
      .subscribe(
        ([userId]) => {
          this.store.dispatch(
            new UserActions.RecoverOrderApproval({
              userId,
              orderApprovalCode,
              cartId,
            })
          );
        },
        () => {}
      );
  }

  /**
   * Abort order approval hijack and restore current users original cart
   *
   * @param orderApprovalCode of the order approval
   * @param cartId of the cart current hijacked cart from the order approval
   */
  abortOrderApprovalHijack(orderApprovalCode: string, cartId: string): void {
    this.userIdService
      .takeUserId(true)
      .pipe(
        withLatestFrom(
          this.userService.isApprover(),
          this.userService.isApprovalAdmin(),
          this.getAbortOrderApprovalHijackLoading(cartId)
        )
      )
      .subscribe(
        ([userId, isApprover, isAdmin, running]) => {
          if (!running && (isApprover || isAdmin)) {
            this.store.dispatch(
              new UserActions.AbortOrderApprovalHijack({
                userId,
                cartId,
                orderApprovalCode,
              })
            );
          }
        },
        () => {}
      );
  }

  deleteOrderApproval(orderApprovalCode: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.DeleteOrderApproval({ userId, orderApprovalCode })),
      () => {}
    );
  }

  getDeleteOrderApprovalLoading(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getDeleteOrderApprovalLoading(orderApprovalCode)));
  }

  /**
   * Load order approval detail for details page
   *
   * @param orderNumber
   */
  loadOrderApprovalDetail(orderNumber: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.LoadOrderApprovalDetails({
            userId,
            orderNumber,
          })
        ),
      () => {}
    );
  }

  /**
   * Get order approval detail for details page
   */
  getOrderApprovalDetail(orderNumber: string): Observable<OrderApproval> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getOrderApprovalDetails(orderNumber));
  }

  /**
   * Get order approval detail loading for details page
   */
  getOrderApprovalDetailLoading(orderNumber: string): Observable<boolean> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getOrderApprovalDetailsLoading(orderNumber));
  }

  /**
   * Initiate a load of B2BComments of order
   *
   * @param code should be SAP order number in status APPROVED or order approval code when in another status
   * @param sapOrder boolean indicator whether SAP order or Hybris order
   */
  loadB2BComments(code: string, sapOrder: boolean): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.LoadB2BComments({
            userId,
            code,
            sapOrder,
          })
        ),
      () => {}
    );
  }

  /**
   * Get the latest loaded b2b comments of order
   *
   * @param code should be SAP order number in status APPROVED or order approval code when in another status
   */
  getB2BComments(code: string): Observable<Array<B2BComment>> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getB2BComments(code));
  }

  getHasApprovalOrderLineErrors(code: string): Observable<boolean> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getHasApprovalOrderLineErrors(code));
  }

  /**
   * Request order processing status
   *
   * @param orderApprovalCode of order being processed
   */
  loadOrderProcessing(orderApprovalCode: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.LoadOrderProcessing({
            userId,
            orderApprovalCode,
          })
        ),
      () => {}
    );
  }

  /**
   * Get latest known order processing state of order
   *
   * @param orderApprovalCode of order
   */
  getOrderProcessing(orderApprovalCode: string): Observable<OrderProcessStatus> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getOrderProcessing(orderApprovalCode)).pipe(
      delayWhen((orderProcessStatus) => {
        const delay = this.determineOrderProcessCheckDelay(orderProcessStatus);
        if (delay > 0) {
          return interval(delay);
        }
        return of(0);
      }),
      tap((ps: OrderProcessStatus) => {
        if (!ps || ps?.orderProcessState === OrderProcessState.RUNNING) {
          this.loadOrderProcessing(orderApprovalCode);
        }
      }),
      filter((ps: OrderProcessStatus) => {
        return ps && ps.orderProcessState !== OrderProcessState.RUNNING && ps.orderStatus === OrderStatus.APPROVED;
      })
    );
  }

  /**
   * Initiate a load simulation of an incomplete pending order
   *
   * USE for orders in status PENDING_APPROVAL
   *
   * @param orderApprovalCode of the pending order
   */
  loadOrderApprovalSimulation(orderApprovalCode: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.LoadOrderApprovalSimulation({
            userId,
            orderApprovalCode,
          })
        ),
      () => {}
    );
  }

  /**
   * Get the latest simulated pending order
   *
   * USE for orders in status PENDING_APPROVAL
   */
  getOrderApprovalSimulation(orderApprovalCode: string): Observable<CartSimulation> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getOrderApprovalSimulation(orderApprovalCode));
  }

  /**
   * Update the search parameters for the approval order search
   *
   * @param searchParams
   */
  updateSearchParameters(searchParams: OrderApprovalSearchParams) {
    this.store.dispatch(new SearchAction(searchParams, ORDER_APPROVALS));
    this.loadOrderApprovals(searchParams);
  }

  /**
   * Get the latest search parameters for the approval order search
   */
  getSearchParameters(): Observable<OrderApprovalSearchParams> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getSearchParamsSelector).pipe(
      map((params) => params as OrderApprovalSearchParams),
      filter((params) => params && !!params.key)
    );
  }

  getPagination(): Observable<Pagination> {
    return this.store.select(getSearchPaginationSelector);
  }

  /**
   * Load approval orders with the latest search parameters
   *
   * @param params of the search
   */
  loadOrderApprovals(params: OrderApprovalSearchParams): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadOrderApprovals(userId, params)),
      () => {}
    );
  }

  /**
   * Get the last search result of the provided search parameters.
   * Will initiate a new search if no initial search data present.
   *
   * @param params of the search
   */
  getOrderApprovals(_params?: OrderApprovalSearchParams): Observable<Array<Observable<OrderApproval>>> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());
    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.store.select(UserSelectors.OrderApprovalSelectors.getSearchPageResultsSelector)),
      map((loaderState) => loaderState?.value?.results || []),
      map((results) =>
        results.map((key) => this.store.select(UserSelectors.OrderApprovalSelectors.getSearchResultEntry, { key }))
      ),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn)))
    );
  }

  getOrderApprovalsLoading(): Observable<boolean> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getSearchPageResultsLoading);
  }

  /**
   * Make a decision of a pending approval order if not already loading a decision result
   *
   * @param orderApprovalCode of the pending order
   * @param orderApprovalDecision to be made on the order
   */
  makeDecision(orderApprovalCode: string, orderApprovalDecision: OrderApprovalDecision): void {
    this.activeCartService
      .getActiveCart(CartType.stock)
      .pipe(
        filter((cart) => !!cart),
        withLatestFrom(
          this.getMakeDecisionResultLoading(orderApprovalCode),
          this.userService.isApprovalAdmin(),
          this.userService.isApprover(),
          this.userIdService.getUserId()
        ),
        take(1)
      )
      .subscribe(
        ([cart, running, isAdmin, isApprover, userId]) => {
          if (!cart || userId === OCC_USER_ID_ANONYMOUS) {
            return;
          }
          const canPerformActions = isAdmin || isApprover;
          const disallowedHijack =
            orderApprovalDecision.decision === OrderApprovalDecisionValue.HIJACK_ORDER &&
            cart.status === OrderStatus.APPROVER_EDITING;

          if (!running && !disallowedHijack && canPerformActions) {
            this.store.dispatch(
              new UserActions.MakeDecision({
                userId,
                orderApprovalCode,
                orderApprovalDecision,
              })
            );
          } else if (disallowedHijack) {
            this.globalMessageService.add(
              {
                key: 'orderApproval.editFailure_message',
              },
              GlobalMessageType.MSG_TYPE_ERROR
            );
            this.store.dispatch(
              new UserActions.MakeDecisionFail({
                orderApprovalCode: orderApprovalCode,
                showErrors: false,
              })
            );
          }
        },
        () => {}
      );
  }

  /**
   * Returns the makeDecision loading flag.  Returns true when the process triggered
   * by makeDecision() is currently running.
   */
  getMakeDecisionResultLoading(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getMakeDecisionLoading(orderApprovalCode)));
  }

  /**
   * Returns the makeDecision failure outcome.  Returns true when the outcome
   * of makeDecision() was an error.
   */
  getMakeDecisionResultError(orderApprovalCode: string): Observable<LoaderError | undefined> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getMakeDecisionError(orderApprovalCode)));
  }

  /**
   * Returns the makeDecision process success outcome.  Returns true when the outcome
   * of makeDecision() was a success.
   */
  getMakeDecisionResultSuccess(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getMakeDecisionSuccess(orderApprovalCode)));
  }

  /**
   * Resets the makeDecision process state. It is usually preferable to reset the
   * process state before making a call to makeDecision() for which we then want
   * to monitor the loading state or the outcome.
   */
  resetMakeDecisionProcessState(orderApprovalCode: string): void {
    this.store.dispatch(new UserActions.MakeDecisionReset(orderApprovalCode));
  }

  getOrderApprovalDetailsLoading(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(
      select(UserSelectors.OrderApprovalSelectors.getApprovedOrderApprovalDetailsLoading(orderApprovalCode))
    );
  }

  getOrderApprovalDetailsError(orderApprovalCode: string): Observable<LoaderError | undefined> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getApprovedOrderApprovalDetailsError(orderApprovalCode)));
  }

  getOrderApprovalDetailsSuccess(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(
      select(UserSelectors.OrderApprovalSelectors.getApprovedOrderApprovalDetailsSuccess(orderApprovalCode))
    );
  }

  resetApprovedOrderApprovalDetailsState(orderApprovalCode: string): void {
    this.store.dispatch(new UserActions.LoadApprovedOrderApprovalDetailsReset(orderApprovalCode));
  }

  getOrderApprovalLoading(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getOrderApprovalLoading(orderApprovalCode)));
  }

  getOrderApprovalError(orderApprovalCode: string): Observable<LoaderError | undefined> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getOrderApprovalError(orderApprovalCode)));
  }

  getOrderApprovalSuccess(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getOrderApprovalSuccess(orderApprovalCode)));
  }

  resetOrderApprovalState(orderApprovalCode: string): void {
    this.store.dispatch(new UserActions.LoadOrderApprovalReset(orderApprovalCode));
  }

  getRecoverOrderApprovalLoading(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getOrderApprovalRecoveryLoading(orderApprovalCode)));
  }

  getRecoverOrderApprovalError(orderApprovalCode: string): Observable<LoaderError | undefined> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getOrderApprovalRecoveryError(orderApprovalCode)));
  }

  getRecoverOrderApprovalSuccess(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getOrderApprovalRecoverySuccess(orderApprovalCode)));
  }

  resetRecoverOrderApprovalState(orderApprovalCode: string): void {
    this.store.dispatch(new UserActions.RecoverOrderApprovalReset(orderApprovalCode));
  }

  getOrderApprovalSimulationLoading(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getOrderApprovalSimulationLoading(orderApprovalCode)));
  }

  getOrderApprovalSimulationError(orderApprovalCode: string): Observable<LoaderError | undefined> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getOrderApprovalSimulationError(orderApprovalCode)));
  }

  getOrderApprovalSimulationSuccess(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getOrderApprovalSimulationSuccess(orderApprovalCode)));
  }

  resetOrderApprovalSimulationState(orderApprovalCode: string): void {
    this.store.dispatch(new UserActions.LoadOrderApprovalSimulationReset(orderApprovalCode));
  }

  getB2BCommentsLoading(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getB2BCommentsLoading(orderApprovalCode)));
  }

  getB2BCommentsError(orderApprovalCode: string): Observable<LoaderError | undefined> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getB2BCommentsError(orderApprovalCode)));
  }

  getB2BCommentsSuccess(orderApprovalCode: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getB2BCommentsSuccess(orderApprovalCode)));
  }

  resetB2BCommentsState(orderApprovalCode: string): void {
    this.store.dispatch(new UserActions.LoadB2BCommentsReset(orderApprovalCode));
  }

  getAbortOrderApprovalHijackLoading(cartId: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getAbortOrderApprovalHijackLoading(cartId)));
  }

  getAbortOrderApprovalHijackError(cartId: string): Observable<LoaderError | undefined> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getAbortOrderApprovalHijackError(cartId)));
  }

  getAbortOrderApprovalHijackSuccess(cartId: string): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.OrderApprovalSelectors.getAbortOrderApprovalHijackSuccess(cartId)));
  }

  resetAbortOrderApprovalHijackState(cartId: string): void {
    this.store.dispatch(new UserActions.AbortOrderApprovalHijackReset(cartId));
  }

  resetOrderApprovalDetailsState(orderApprovalCode: string): void {
    this.store.dispatch(new UserActions.LoadOrderApprovalDetailsReset(orderApprovalCode));
  }

  getSorts(): Observable<Sort[]> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getSortsSelector);
  }

  searchForAvailableBuyers(searchParams: AvailableBuyersSearchParams): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadAvailableOrderApprovalsBuyers(searchParams, userId)),
      () => {}
    );
  }

  getSearchParametersForAvailableBuyers(): Observable<AvailableBuyersSearchParams> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getAvailableBuyerSearchParamsSelector).pipe(
      map((params) => params as AvailableBuyersSearchParams),
      tap((params: AvailableBuyersSearchParams) => {
        if (!params || !params.key) {
          this.updateSearchParametersForAvailableBuyers({
            key: 'latest',
            page: 0,
            count: 25,
          });
        }
      }),
      filter((params) => params && !!params.key)
    );
  }

  updateSearchParametersForAvailableBuyers(searchParams: AvailableBuyersSearchParams) {
    this.store.dispatch(new SearchAction(searchParams, ORDER_APPROVAL_BUYERS));
  }

  getAvailableBuyers(): Observable<Array<SimpleCustomer>> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getAvailableBuyerSearchPageResultsSelector).pipe(
      filter((loaderState) => loaderState.success),
      map((loaderState) => loaderState.value.results),
      switchMap((results) =>
        this.store.select(UserSelectors.OrderApprovalSelectors.getAvailableBuyerSearchResultEntries, { keys: results })
      )
    );
  }

  getAvailableBuyersLoading(): Observable<boolean> {
    return this.store
      .select(UserSelectors.OrderApprovalSelectors.getAvailableBuyerSearchPageResultsSelector)
      .pipe(map((loaderState) => loaderState.loading));
  }

  getAvailableBuyersPagination(): Observable<Pagination | undefined> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getAvailableBuyerSearchPaginationSelector);
  }

  private determineOrderProcessCheckDelay(orderProcessStatus): number {
    return this.shouldWait(orderProcessStatus) ? this.config.checkout.processingInterval * 1000 : 0;
  }

  /**
   * Duplicated from checkout processing service. Can probably be refactored in the
   * future so that the process status check is generic enough to be used in both
   * of these places. Checkout and approval. However, in order to minimize the moving
   * parts during the implementation of this I choose to copy it here.
   *
   * @param orderProcessStatus
   * @private
   */
  private shouldWait(orderProcessStatus: OrderProcessStatus): boolean {
    if (orderProcessStatus === undefined) {
      return false;
    }
    switch (orderProcessStatus.orderProcessState) {
      case OrderProcessState.RUNNING:
        return true;
      case OrderProcessState.WAITING:
        switch (orderProcessStatus.orderStatus) {
          case OrderStatus.PENDING_APPROVAL:
          case OrderStatus.ASSIGNED_TO_ADMIN:
          case OrderStatus.CANCELLED:
          case OrderStatus.PAYMENT_NOT_AUTHORIZED:
            return false;
          default:
            return true;
        }
      default:
        return false;
    }
  }

  getStatusForOrderApprovalCode(orderCode: string): Observable<string> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => this.orderApprovalConnector.getOrderApprovalDetails(userId, orderCode)),
      map((order) => order?.status)
    );
  }
}
